import React, { useCallback, useRef, useState } from 'react'
import { Button, Col, Form, Popover, Row, Space } from 'antd'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { CaretDownOutlined } from '@ant-design/icons'
import { isEqual } from 'lodash-es'

import { formatDateText } from 'utils/formatting'
import { filterOption } from 'utils/helper'
import { Filter as ButtonFilter, RangePicker, SearchWidget, Select } from 'components/UI'
import { transTypeConfigSelector } from 'app-redux/selectors'

// map with name to make easy what are the ids is
export const excludeTransTypeIdsForAccountTransactions = [
  {
    id: 2,
    name: 'Purchase Order',
  },
  {
    id: 4,
    name: 'Quote',
  },
  {
    id: 6,
    name: 'Order',
  },
  {
    id: 51,
    name: 'Warehouse Transfer',
  },
  {
    id: 63,
    name: 'Purchase Quote',
  },
]

const Filter = ({
  isMobileView,
  params,
  showClearBtn,
  onToggleGraph,
  onSearch,
  showGraph,
  showSearch,
  setParams,
  onClear,
  initialParams,
}) => {
  const [search, setSearch] = useState('')

  const filterFormRef = useRef(null)

  const { formatMessage } = useIntl()
  const transTypeIds = useSelector(transTypeConfigSelector)

  const onChange = useCallback(
    (value) => {
      setParams({ date_from: value[0], date_to: value[1] })
    },
    [setParams],
  )

  const filterVisibleChangeHandler = useCallback(
    async (visible) => {
      if (visible) {
        return
      }

      const form = filterFormRef.current
      try {
        const values = await form.validateFields()

        if (!isEqual(values.sources_id, params?.sources_id)) {
          setParams(values)
        }
      } catch (info) {
        form.scrollToField(info.errorFields[0].name)
      }
    },
    [setParams, params],
  )

  const renderFilter = useCallback(() => {
    const filteredTransTypeIds = (transTypeIds || []).filter(
      (transTypeId) =>
        !excludeTransTypeIdsForAccountTransactions.find(
          (_transType) => transTypeId.id === _transType.id,
        ),
    )

    return (
      <Form
        style={{ width: 300 }}
        name="FilterFormAccountTransaction"
        ref={filterFormRef}
        initialValues={{ sources_id: params.sources_id || '' }}
        layout="vertical"
      >
        <Form.Item name="sources_id" label={formatMessage({ id: 'report.source' })}>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder={formatMessage({ id: 'report.please_select_source' })}
            filterOption={filterOption}
            options={filteredTransTypeIds}
            fieldNames={{ label: 'name', value: 'id' }}
          />
        </Form.Item>
      </Form>
    )
  }, [formatMessage, params.sources_id, transTypeIds])

  const clearFilterHandler = useCallback(() => {
    onClear()
    if (filterFormRef.current) {
      filterFormRef.current.setFieldsValue({ ...initialParams })
    }
  }, [onClear, initialParams])

  const searchChangeHandler = useCallback(
    (event) => {
      setSearch(event.target.value)
      onSearch(event)
    },
    [onSearch],
  )

  return (
    <Row>
      <Col xs={24} md={12}>
        <Space>
          {showSearch && (
            <Popover
              style={{ alignItems: 'center' }}
              placement="bottomLeft"
              content={renderFilter()}
              trigger="click"
              onOpenChange={filterVisibleChangeHandler}
              getPopupContainer={() => document.body}
            >
              <ButtonFilter onClear={clearFilterHandler} showClearBtn={showClearBtn} />
            </Popover>
          )}
          <Button
            icon={
              <CaretDownOutlined
                style={{
                  transform: `rotate(${showGraph ? 0 : 180}deg)`,
                  transition: 'all 150ms ease-in-out',
                }}
              />
            }
            onClick={onToggleGraph}
          >
            {formatMessage({ id: showGraph ? 'reports.hide_graph' : 'reports.show_graph' })}
          </Button>
        </Space>
      </Col>
      <Col style={isMobileView ? { marginTop: 10 } : { textAlign: 'right' }} xs={24} md={12}>
        <Space>
          {showSearch && (
            <SearchWidget
              onChange={searchChangeHandler}
              placeholder={formatMessage({ id: 'input.placeholder.search' })}
              value={search}
            />
          )}

          <RangePicker
            format={formatDateText()}
            value={[params.date_from, params.date_to]}
            placeholder="End"
            allowClear={false}
            onChange={onChange}
            style={{ width: '100%' }}
          />
        </Space>
      </Col>
    </Row>
  )
}

export default React.forwardRef((props, ref) => <Filter innerRef={ref} {...props} />)
